import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { getAuditLogs } from 'services/transparency-config-service';
import { formatDateTimeWithTimezone } from 'utils/date_utils';
import { LOCALE_MAP } from 'utils/locale-formatting';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import { StatusChip } from 'Components/common/StatusChip';
import styled from 'styled-components';
import { Skeleton } from 'Components/common/Skeleton';
import { Select, TextField } from 'Components/ui';
import dayjs from 'dayjs';

const Card = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`

const Title = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px;
`

const Subtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0 0 20px;
`


const FiltersBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  padding: 16px 20px;
  background: var(--vo-mist);
  border-radius: 10px;
  margin-bottom: 20px;
`

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const FilterLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-slate);
`



const FilterBtnGroup = styled.div`
  display: flex;
  gap: 8px;
`

const FilterBtn = styled.button`
  height: 40px;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  border: ${({ $variant }) => $variant === 'secondary' ? '1.5px solid var(--vo-line)' : 'none'};
  background: ${({ $variant }) => $variant === 'secondary' ? 'white' : 'var(--vo-celeste)'};
  color: ${({ $variant }) => $variant === 'secondary' ? 'var(--vo-slate)' : 'white'};
  &:hover { background: ${({ $variant }) => $variant === 'secondary' ? 'var(--vo-mist)' : 'var(--vo-cobalto)'}; }
`

const TableBorder = styled.div`
  border: 1px solid var(--vo-cloud);
  border-radius: 10px;
  overflow: hidden;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const TableHead = styled.thead`
  background: var(--vo-mist);
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:last-child { border-bottom: none; }
  &:hover { background: var(--vo-capri-25); }
`

const TableCell = styled.td`
  padding: 12px 16px;
  vertical-align: middle;
  text-align: ${({ $align }) => $align || 'left'};
`

const TableHeaderCell = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: ${({ $align }) => $align || 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  white-space: nowrap;
`

const IconFlex = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
`

const AlertError = styled(Alert)`
  background-color: var(--vo-danger-10);
  color: var(--vo-danger);
`

const AlertInfo = styled(Alert)`
  background-color: var(--vo-info-8);
  color: var(--vo-celeste);
`

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 12px 0;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`

const PaginationBtn = styled.button`
  padding: 4px 10px;
  border: 1px solid var(--vo-cloud);
  border-radius: 8px;
  background: white;
  color: var(--vo-ink-2);
  font-size: 13px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  &:hover:not(:disabled) { background: var(--vo-mist); }
`

const FailureText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${({ $isError }) => ($isError ? 'var(--vo-danger)' : 'var(--vo-slate)')};
`

const SearchSVG = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
)

const EventTypeMeta = {
  token_created: { color: 'operational' },
  token_used: { color: 'info' },
  token_revoked: { color: 'warning' },
  public_access: { color: 'info' },
  access_denied: { color: 'error' },
  general_settings: { color: 'private' },
  framework_visibility: { color: 'private' },
  policy_visibility: { color: 'private' },
  access_control: { color: 'private' },
};

const CheckCircleSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={'var(--vo-success)'}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);

const CancelSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={'var(--vo-danger)'}>
    <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" />
  </svg>
);

const SettingsSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={'var(--vo-slate)'}>
    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
  </svg>
);

const SecuritySvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={'var(--vo-slate)'}>
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
  </svg>
);

const VisibilitySvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={'var(--vo-slate)'}>
    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
  </svg>
);

const BlockSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={'var(--vo-slate)'}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z" />
  </svg>
);

const PublicSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={'var(--vo-slate)'}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
  </svg>
);

const EVENT_ICONS = {
  token_created: SettingsSvg,
  token_used: VisibilitySvg,
  token_revoked: BlockSvg,
  public_access: PublicSvg,
  access_denied: CancelSvg,
  general_settings: SettingsSvg,
  framework_visibility: SettingsSvg,
  policy_visibility: SettingsSvg,
  access_control: SecuritySvg,
};

export const AuditLog = ({ companyKey }) => {
  const selectedTenant = useSelector(useAuthTenant);
  const companyLocale = LOCALE_MAP[selectedTenant?.defaultLanguage] ?? 'pt-BR';
  const { t } = useTranslation(['transparency', 'common']);

  const [filters, setFilters] = useState({
    startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    eventType: '',
    page: 1,
    perPage: 50,
  });

  const { data, isLoading, error } = useQuery(
    ['auditLogs', companyKey, filters],
    // 'all' is the Select's explicit "All events" choice, rendered full-strength
    // to distinguish it from a genuinely untouched filter — normalized back to
    // '' here so the wire-level query param is unchanged either way.
    () => getAuditLogs(companyKey, { ...filters, eventType: filters.eventType === 'all' ? '' : filters.eventType }),
    {
      enabled: !!companyKey,
      keepPreviousData: true,
      staleTime: 30 * 1000,
    }
  );

  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
      page: key !== 'page' ? 1 : value,
    });
  };

  const clearFilters = () => {
    setFilters({
      startDate: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      eventType: '',
      page: 1,
      perPage: 50,
    });
  };

  const handleChangePage = (newPage) => {
    handleFilterChange('page', newPage + 1);
  };

  const handleChangeRowsPerPage = (value) => {
    handleFilterChange('perPage', parseInt(value, 10));
  };

  const getEventConfig = (eventType) => {
    const meta = EventTypeMeta[eventType];
    if (!meta) return { label: eventType, color: 'private' };
    return { ...meta, label: t(`transparency:auditLog.events.${eventType}`) };
  };

  if (isLoading) {
    return (
      <Card>
        <Skeleton $width={300} $height={40} $marginBottom={8} />
        <Skeleton $width={'100%'} $height={100} $marginBottom={16} />
        <Skeleton $width={'100%'} $height={400} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <AlertError>{t('transparency:auditLog.error')}</AlertError>
      </Card>
    );
  }

  const logs = data?.data?.logs || [];
  const total = data?.data?.total || 0;
  const totalPages = Math.ceil(total / filters.perPage);
  const currentPage = filters.page - 1;

  return (
    <Card>
      <Title>{t('transparency:auditLog.title')}</Title>
      <Subtitle>{t('transparency:auditLog.subtitle')}</Subtitle>

      <FiltersBox>
        <FilterField>
          <FilterLabel htmlFor="startDate">{t('transparency:auditLog.filters.startDate')}</FilterLabel>
          <TextField
            fullWidth
            id="startDate"
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </FilterField>
        <FilterField>
          <FilterLabel htmlFor="endDate">{t('transparency:auditLog.filters.endDate')}</FilterLabel>
          <TextField
            fullWidth
            id="endDate"
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </FilterField>
        <FilterField>
          <FilterLabel htmlFor="eventType">{t('transparency:auditLog.filters.eventType')}</FilterLabel>
          <Select
            name="eventType"
            value={filters.eventType}
            onChange={(value) => handleFilterChange('eventType', value)}
            options={[
              { value: '', label: t('transparency:auditLog.filters.selectEventType') },
              { value: 'all', label: t('transparency:auditLog.filters.allEvents') },
              ...Object.keys(EventTypeMeta).map((key) => ({ value: key, label: t(`transparency:auditLog.events.${key}`) })),
            ]}
          />
        </FilterField>
        <FilterBtnGroup>
          <FilterBtn $variant="secondary" onClick={clearFilters}>
            {t('transparency:auditLog.filters.clear')}
          </FilterBtn>
          <FilterBtn onClick={() => handleFilterChange('page', 1)}>
            <SearchSVG />
            {t('transparency:auditLog.filters.apply')}
          </FilterBtn>
        </FilterBtnGroup>
      </FiltersBox>

      {logs.length > 0 ? (
        <>
          <TableBorder>
            <Table>
              <TableHead>
                <tr>
                  <TableHeaderCell>{t('transparency:auditLog.table.datetime')}</TableHeaderCell>
                  <TableHeaderCell>{t('transparency:auditLog.table.eventType')}</TableHeaderCell>
                  <TableHeaderCell>{t('transparency:auditLog.table.actor')}</TableHeaderCell>
                  <TableHeaderCell>{t('transparency:auditLog.table.recipientEmail')}</TableHeaderCell>
                  <TableHeaderCell $align="center">{t('transparency:auditLog.table.status')}</TableHeaderCell>
                  <TableHeaderCell>{t('transparency:auditLog.table.details')}</TableHeaderCell>
                </tr>
              </TableHead>
              <TableBody>
                {logs.map((log, index) => {
                  const eventConfig = getEventConfig(log.eventType);
                  const EventIcon = EVENT_ICONS[log.eventType] || SettingsSvg;
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <span style={{ fontFamily: "'Lato',sans-serif", fontSize: '12px', color: 'var(--vo-slate)' }}>
                          {formatDateTimeWithTimezone(log.timestamp, selectedTenant?.timezone, companyLocale)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <IconFlex>
                          <EventIcon />
                          <StatusChip label={eventConfig.label} variant={eventConfig.color} />
                        </IconFlex>
                      </TableCell>
                      <TableCell>
                        <span style={{ fontFamily: "'Lato',sans-serif", fontSize: '13px', color: 'var(--vo-ink-2)' }}>
                          {log.actorEmail || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span style={{ fontFamily: "'Lato',sans-serif", fontSize: '13px', color: 'var(--vo-ink-2)' }}>
                          {log.recipientEmail || '-'}
                        </span>
                      </TableCell>
                      <TableCell $align="center">
                        {log.success ? <CheckCircleSvg /> : <CancelSvg />}
                      </TableCell>
                      <TableCell>
                        <FailureText $isError={!log.success}>
                          {log.failureReason || '-'}
                        </FailureText>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableBorder>

          <Pagination>
            <span>
              {t('transparency:auditLog.pagination.rowsPerPage')}
              <Select
                name="perPage"
                value={filters.perPage}
                onChange={handleChangeRowsPerPage}
                options={[25, 50, 100].map((n) => ({ value: n, label: String(n) }))}
                sx={{ marginLeft: '4px', '.MuiOutlinedInput-input': { padding: '4px 8px', fontSize: 13 } }}
              />
            </span>
            <span>
              {currentPage * filters.perPage + 1}-{Math.min((currentPage + 1) * filters.perPage, total)}
              {' '}{t('transparency:auditLog.pagination.of')}{' '}
              {total}
            </span>
            <PaginationBtn onClick={() => handleChangePage(currentPage - 1)} disabled={currentPage === 0}>&lt;</PaginationBtn>
            <PaginationBtn onClick={() => handleChangePage(currentPage + 1)} disabled={currentPage + 1 >= totalPages}>&gt;</PaginationBtn>
          </Pagination>
        </>
      ) : (
        <AlertInfo>{t('transparency:auditLog.empty')}</AlertInfo>
      )}
    </Card>
  );
};
